import React from 'react';
import { useSelector } from 'react-redux';
import CellValidationStrip from './CellValidationStrip';
import {
  VALIDATION_STATE_LABELS,
  isCuratorValidationTet,
} from '../helpers/groupTets';
import { speciesBadgeLetter, speciesName } from '../helpers/speciesUtils';

/** TETs from this cell that are *topic-level* and authored by a professional
 *  biocurator (i.e. count as a curator validation). */
function professionalBiocuratorTopicTets(tets) {
  const arr = Array.isArray(tets) ? tets : (tets?.tets || []);
  return arr.filter(isCuratorValidationTet);
}

/** Derive the validation summary for a cell from its raw tets (the fallback
 *  path used when the batch endpoint did not aggregate validation server-side).
 *  Returns null when the cell has no curator validation.
 *
 *  Shape mirrors the server `validation` aggregate exactly so the renderer is
 *  agnostic to where the summary came from:
 *    { positives, negatives, byCurator: [{ name, negated,
 *        sources: [{ method, label }], species: [curie] }] } */
function deriveValidationSummary(tets) {
  const validations = professionalBiocuratorTopicTets(tets);
  if (validations.length === 0) return null;
  const positives = validations.filter((t) => !t.negated).length;
  const negatives = validations.filter((t) => t.negated).length;

  // Group validations by (curator, polarity) and collect the source methods
  // each (curator, polarity) combination used. If Curator A submitted both
  // a positive and a negative tag they appear on both rows; multiple
  // positives from the same curator collapse into one row but the source
  // icons accumulate. We never drop a name even if created_by is missing —
  // render as "(unknown curator)" instead, so every validation TET is
  // accounted for.
  const groups = new Map();
  for (const t of validations) {
    const name = t.created_by || '(unknown curator)';
    const negated = !!t.negated;
    const key = `${name}|${negated ? 'N' : 'Y'}`;
    if (!groups.has(key)) {
      groups.set(key, { name, negated, sources: new Map(), species: new Set() });
    }
    const src = t.tag_source?.source_method;
    if (src) {
      // Keep a Map source_method → label so the tooltip shows the full
      // source label (method / data-provider) while the icon shows just
      // the first letter.
      const lab = `${src}${
        t.tag_source?.secondary_data_provider_abbreviation
          ? ` / ${t.tag_source.secondary_data_provider_abbreviation}`
          : ''
      }`;
      groups.get(key).sources.set(src, lab);
    }
    if (t.species) groups.get(key).species.add(t.species);
  }
  const byCurator = [...groups.values()].map((g) => ({
    name: g.name,
    negated: g.negated,
    sources: [...g.sources.entries()].map(([method, label]) => ({
      method,
      label,
    })),
    species: [...g.species],
  }));
  return { positives, negatives, byCurator };
}

/** Normalize the server `validation` aggregate into the renderer's summary
 *  shape. Returns null when the server reports no curator validation. */
function serverValidationSummary(cell) {
  if (!cell) return null;
  return {
    positives: cell.positives || 0,
    negatives: cell.negatives || 0,
    byCurator: (cell.by_curator || []).map((g) => ({
      name: g.name || '(unknown curator)',
      negated: !!g.negated,
      sources: Array.isArray(g.sources) ? g.sources : [],
      species: Array.isArray(g.species) ? g.species : [],
    })),
  };
}

export default function ValidationCell(params) {
  const tets = Array.isArray(params.value)
    ? params.value
    : (params.value?.tets || []);
  const {
    topicCurie,
    topicName,
    onValidated,
    curationStatusOptions,
    curationTagOptions,
  } = params.colDef.cellRendererParams || {};
  const referenceCurie = params.data?.curie;
  const curieToNameTaxon = useSelector(
    (s) => s.biblio.curieToNameTaxon
  );

  // Prefer the server-aggregated validation cell when present so this column
  // renders without re-deriving from raw tags. `undefined` => the row was not
  // server-aggregated (older backend / per-ref fallback), derive from tets;
  // `null` => the server aggregated the row and found no curator validation.
  const serverCell = Array.isArray(params.value)
    ? undefined
    : params.value?.validation;
  const summary =
    serverCell !== undefined
      ? serverValidationSummary(serverCell)
      : deriveValidationSummary(tets);

  if (summary) {
    const { positives, negatives, byCurator: uniquePairs } = summary;

    const firstWord = (s) => String(s || '').split(/[_\s/]/)[0];
    const SourceIcons = ({ sources }) => (
      <>
        {sources.map((s) => (
          <span
            className="tetv-source-icon"
            key={s.method}
            title={`source: ${s.label}`}
            aria-label={`source: ${s.label}`}
          >
            {firstWord(s.method) || '?'}
          </span>
        ))}
      </>
    );
    const SpeciesBadges = ({ taxonCuries }) => (
      <>
        {taxonCuries.map((curie) => {
          const name = speciesName(curieToNameTaxon, curie);
          return (
            <span
              className="tetv-species-icon"
              key={curie}
              title={`species: ${name}`}
              aria-label={`species: ${name}`}
            >
              {speciesBadgeLetter(curieToNameTaxon, curie)}
            </span>
          );
        })}
      </>
    );

    let label, cls, tooltip, attribution;
    if (positives > 0 && negatives > 0) {
      label = VALIDATION_STATE_LABELS.conflict;
      cls = 'tetv-validation-status tetv-validated-conflict';
      tooltip =
        `${positives} Y + ${negatives} N professional ` +
        `biocurator topic tag(s) on this cell`;
      attribution = (
        <div className="tetv-validation-by-list">
          {uniquePairs.map((p) => (
            <div className="tetv-validation-by" key={`${p.name}|${p.negated}`}>
              <span
                className={`tetv-validation-by-mark ${
                  p.negated
                    ? 'tetv-validation-by-neg'
                    : 'tetv-validation-by-pos'
                }`}
                title={
                  p.negated
                    ? 'assessed N (no data for this topic)'
                    : 'assessed Y (data present for this topic)'
                }
              >
                {p.negated ? 'N' : 'Y'}
              </span>{' '}
              {p.name}
              {p.species.length > 0 && (
                <>{' '}<SpeciesBadges taxonCuries={p.species} /></>
              )}
              {p.sources.length > 0 && (
                <>{' '}<SourceIcons sources={p.sources} /></>
              )}
            </div>
          ))}
        </div>
      );
    } else if (positives > 0 || negatives > 0) {
      const isPositive = positives > 0;
      label = isPositive
        ? VALIDATION_STATE_LABELS.positive
        : VALIDATION_STATE_LABELS.negative;
      cls = isPositive
        ? 'tetv-validation-status tetv-validated-pos'
        : 'tetv-validation-status tetv-validated-neg';
      tooltip = isPositive
        ? `Y (data present) — ${positives} professional biocurator topic tag(s)`
        : `N (no data) — ${negatives} professional biocurator topic tag(s)`;
      attribution =
        uniquePairs.length > 0 ? (
          <div className="tetv-validation-by-list">
            {uniquePairs.map((p) => (
              <div
                className="tetv-validation-by"
                key={`${p.name}|${p.negated}`}
              >
                {p.name}
                {p.species.length > 0 && (
                  <>{' '}<SpeciesBadges taxonCuries={p.species} /></>
                )}
                {p.sources.length > 0 && (
                  <>{' '}<SourceIcons sources={p.sources} /></>
                )}
              </div>
            ))}
          </div>
        ) : null;
    }
    return (
      <div className="tetv-validation-cell">
        <span className={cls} title={tooltip}>{label}</span>
        {attribution}
      </div>
    );
  }

  return (
    <div className="tetv-validation-cell">
      <CellValidationStrip
        referenceCurie={referenceCurie}
        topicCurie={topicCurie}
        topicName={topicName}
        cellTets={tets}
        onValidated={onValidated}
        curationStatusOptions={curationStatusOptions}
        curationTagOptions={curationTagOptions}
      />
    </div>
  );
}

export { professionalBiocuratorTopicTets };
